import { FormGroup } from '@angular/forms';

/**
 * The narrowest shape this predicate reads — deliberately NOT
 * `FormControlSchema` from `models/form-schema.model`.
 *
 * role0 imports that model; role1 declares its own local interface with the
 * same name but a looser `ui.inputFilter` (`string` vs `InputFilterType`), so
 * the two are not assignable to each other. Accepting a structural subset lets
 * one util serve both sheets without either of them changing its schema type —
 * which is the whole point of centralising the rule.
 */
export interface LockStateControlSchema {
  ui: {
    readonly?: boolean;
    lockWhen?: { field: string; equals: string };
    disabledWhen?: { field: string; values: string[] };
  };
}

/**
 * Which schema rule is asking. The engine currently evaluating excludes
 * ITSELF, because it is about to act on its own condition — it only needs to
 * know whether some OTHER rule still holds the control disabled.
 */
export type DisableRuleKind = 'lockWhen' | 'disabledWhen';

/**
 * Answers: "may this control be re-enabled right now?" — for a control that
 * can be disabled by MORE THAN ONE schema rule.
 *
 * `Basis of accounting standards applied to prepare the financial statements`
 * on role0/role1 is the only such control today, and it carries both:
 *
 * ```json
 * "disabledWhen": { "field": "ssmt-dei:TypeOfCompany",   "values": ["Company limited by guarantee"], … },
 * "lockWhen":     { "field": "ssmt-dei:OriginOfCompany", "equals": "Incorporated outside Malaysia",  … }
 * ```
 *
 * Each engine watches its OWN trigger and, when that trigger stops matching,
 * re-enables the control. Neither used to consult the other, so whichever ran
 * last won — in BOTH directions, and both were live defects (2026-08-14):
 *
 * - **`disabledWhen` clobbering `lockWhen`.** Any change to Types of company
 *   re-enabled Basis while Origin was still "Incorporated outside Malaysia",
 *   handing the user an MFRS/MPERS dropdown the lock exists to forbid (see
 *   `docs/specifications/sheets/00_filing-prerequisites.md` Field #16). The
 *   existing `setupLockWhenReassert` safety net could not catch it: that one is
 *   gated on the target going EMPTY, and a plain re-enable leaves the value
 *   intact — and `enable({ emitEvent: false })` emits nothing to trigger it.
 * - **`lockWhen` clobbering `disabledWhen`.** At mount with Origin
 *   "Incorporated in Malaysia" and Types of company "Company limited by
 *   guarantee", `setupFieldDisabling` disabled Basis and `setupLockWhenRules` —
 *   which runs later in `ngOnInit` on both sheets — immediately re-enabled it.
 *
 * The `applyLockRule` comment asserting that "disabledWhen's own watcher will
 * re-disable on the next emission if its condition still holds" described a
 * mechanism that does not exist: there is no next emission unless that rule's
 * own trigger changes.
 *
 * Both engines now route their else-branch through this predicate, so the
 * control stays disabled while ANY reason to disable it survives, whichever
 * engine happens to run last.
 *
 * `ui.readonly` is folded in as a third, unconditional reason: a readonly
 * control is disabled by construction in `buildForm` and no value-driven rule
 * may ever hand it back to the user.
 *
 * Deliberately DI-free and component-free so role0's and role1's duplicated
 * engines share one answer — that duplication is a documented recurring defect
 * class (see the root `CLAUDE.md` on role0/role1 parity), and this is one of
 * the few places where a single seam removes the drift rather than doubling it.
 *
 * @param fieldSchema the schema record of the control being re-enabled
 * @param form        the sheet's form group, used to read the OTHER rule's trigger
 * @param evaluating  the rule currently running, excluded from the check
 * @returns true when some other rule (or `readonly`) still requires disabled
 */
export function isHeldDisabledByAnotherRule(
  fieldSchema: LockStateControlSchema,
  form: FormGroup,
  evaluating: DisableRuleKind
): boolean {
  const ui = fieldSchema.ui;

  if (ui.readonly) {
    return true;
  }

  if (evaluating !== 'lockWhen' && ui.lockWhen) {
    const trigger = form.get(ui.lockWhen.field);
    if (trigger && trigger.value === ui.lockWhen.equals) {
      return true;
    }
  }

  if (evaluating !== 'disabledWhen' && ui.disabledWhen) {
    const trigger = form.get(ui.disabledWhen.field);
    if (trigger && ui.disabledWhen.values.includes(trigger.value as string)) {
      return true;
    }
  }

  return false;
}
